/**
 * Kalaboost — carte « À combiner avec » + pop-up routine Hair boost.
 *
 * Après Ajouter, l'upsell est au panier. Le pop-up compte les 3 soins
 * réellement présents, propose le pack, et retire 1 unité de chaque soin
 * trouvé (le surplus reste).
 */

import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const ROOT_SELECTOR = '[data-kalaboost-product-upsell]';
const ADD_SELECTOR = '[data-kalaboost-upsell-add]';
const ACCEPT_SELECTOR = '[data-kalaboost-upsell-accept]';
const DECLINE_SELECTOR = '[data-kalaboost-upsell-decline]';
const CLOSE_SELECTOR = '[data-kalaboost-upsell-close]';
const MODAL_SELECTOR = '[data-kalaboost-upsell-modal]';
const SOURCE = 'kalaboost-product-upsell';
const OPEN_CLASS = 'kb-upsell-open';

/**
 * @param {unknown} value
 * @returns {number}
 */
function toId(value) {
  return Number(value) || 0;
}

/**
 * @param {number} cents
 * @returns {string}
 */
function formatMoney(cents) {
  const amount = (Number(cents) || 0) / 100;
  return `${amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/**
 * @param {number} count
 * @returns {Promise<void>}
 */
function waitFrames(count = 2) {
  return new Promise((resolve) => {
    const step = (left) => {
      if (left <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(() => step(left - 1));
    };
    step(count);
  });
}

/**
 * @param {Element} root
 * @returns {object | null}
 */
function readConfig(root) {
  const pairNode = root.querySelector('[data-kalaboost-upsell-config]');
  const textsNode = root.querySelector('[data-kalaboost-upsell-texts]');
  const packNode = root.querySelector('[data-kalaboost-upsell-pack]');
  if (!pairNode?.textContent) return null;

  try {
    const pair = JSON.parse(pairNode.textContent);
    const texts = textsNode?.textContent ? JSON.parse(textsNode.textContent) : {};
    const packConfig = packNode?.textContent ? JSON.parse(packNode.textContent) : {};
    return { ...pair, ...packConfig, texts };
  } catch (error) {
    console.warn('[kalaboost-upsell] Config JSON invalide', error);
    return null;
  }
}

class KalaboostProductUpsell {
  /** @type {Element} */
  #root;

  /** @type {object | null} */
  #config;

  /** @type {HTMLElement | null} */
  #modal;

  /** @type {Element | null} */
  #modalHome;

  /** @type {boolean} */
  #busy = false;

  /** @type {boolean} */
  #open = false;

  /** @type {boolean} */
  #drawerPaused = false;

  /** @type {Element | null} */
  #lastFocus = null;

  /**
   * @param {Element} root
   */
  constructor(root) {
    this.#root = root;
    this.#config = readConfig(root);
    this.#modal = root.querySelector(MODAL_SELECTOR);
    this.#modalHome = this.#modal?.parentElement || root;

    if (!this.#config?.upsell) return;

    this.#root.addEventListener('click', this.#onClick);
    this.#modal?.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest(ADD_SELECTOR) && this.#root.contains(target)) {
      event.preventDefault();
      this.#onAdd();
      return;
    }

    if (!this.#open) return;

    if (target.closest(ACCEPT_SELECTOR)) {
      event.preventDefault();
      this.#onAccept();
      return;
    }

    if (target.closest(DECLINE_SELECTOR) || target.closest(CLOSE_SELECTOR)) {
      event.preventDefault();
      this.#closeAndOpenCart();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.#open) {
      event.preventDefault();
      this.#closeAndOpenCart();
    }
  };

  async #onAdd() {
    if (this.#busy) return;

    this.#setBusy(true);
    this.#setError(this.#root.querySelector('[data-kalaboost-upsell-error]'), '');
    this.#pauseCartDrawer();

    try {
      const upsell = this.#config.upsell;
      let cart = await this.#fetchCart();
      const handles = this.#cartHandles(cart);

      if (upsell?.available !== false && upsell?.handle && !handles.has(upsell.handle)) {
        await this.#addVariant(upsell.variantId);
      }

      cart = await this.#fetchCart();

      if (this.#canOfferPack()) {
        this.#renderRoutine(cart);
        this.#openModal();
      } else {
        this.#resumeCartDrawer();
        this.#openCartDrawer();
      }
    } catch (error) {
      console.warn('[kalaboost-upsell] Ajout impossible', error);
      this.#setError(
        this.#root.querySelector('[data-kalaboost-upsell-error]'),
        this.#config.texts?.error || "Impossible d'ajouter le produit. Réessayez."
      );
      this.#resumeCartDrawer();
    } finally {
      this.#setBusy(false);
    }
  }

  async #onAccept() {
    if (this.#busy) return;

    this.#setBusy(true);
    this.#setError(this.#modal?.querySelector('[data-kalaboost-upsell-modal-error]'), '');

    try {
      await this.#swapToPack();
      this.#closeAndOpenCart();
    } catch (error) {
      console.warn('[kalaboost-upsell] Passage au pack impossible', error);
      this.#setError(
        this.#modal?.querySelector('[data-kalaboost-upsell-modal-error]'),
        this.#config.texts?.error || "Impossible d'ajouter le pack. Réessayez."
      );
    } finally {
      this.#setBusy(false);
    }
  }

  #canOfferPack() {
    return Boolean(this.#config.pack?.variantId && this.#packComponents().length === 3);
  }

  /**
   * @param {object} cart
   */
  #renderRoutine(cart) {
    const state = this.#routineState(cart);
    const missing = state.missingCount;
    const countLabel = missing === 1 ? '1 produit' : `${missing} produits`;

    const title = this.#modal?.querySelector('[data-kalaboost-upsell-title]');
    if (title instanceof HTMLElement) {
      if (missing <= 0) {
        title.textContent = 'Votre routine est complète. Passez au pack pour la brosse offerte.';
      } else {
        title.replaceChildren();
        title.append('Il vous manque ', Object.assign(document.createElement('em'), { textContent: countLabel }), ' sur les 3 de la routine');
      }
    }

    const intro = this.#modal?.querySelector('[data-kalaboost-upsell-intro]');
    if (intro instanceof HTMLElement && this.#config.texts?.intro) {
      intro.textContent = this.#config.texts.intro;
    }

    this.#renderBundle(state);
    this.#setText('[data-kalaboost-upsell-pack-price]', state.packPrice);
    this.#setText('[data-kalaboost-upsell-pack-compare]', state.listPrice);
    const compare = this.#modal?.querySelector('[data-kalaboost-upsell-pack-compare]');
    if (compare instanceof HTMLElement) compare.hidden = state.savingsCents <= 0;
    this.#setText('[data-kalaboost-upsell-pack-note]', this.#config.texts?.packNote || '');

    const save = this.#modal?.querySelector('[data-kalaboost-upsell-save]');
    if (save instanceof HTMLElement) {
      save.replaceChildren();
      if (state.savingsCents > 0) {
        const amount = document.createElement('strong');
        amount.textContent = `− ${formatMoney(state.savingsCents)}`;
        save.append(amount, document.createTextNode(' Économie'));
        save.hidden = false;
      } else {
        save.hidden = true;
      }
    }

    this.#modal.querySelectorAll('[data-kalaboost-upsell-perk]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const template = node.dataset.template || node.textContent || '';
      node.dataset.template = template;
      const hasSavingsToken = template.includes('[economie]');
      const row = node.closest('[data-kalaboost-upsell-perk-row]');
      if (hasSavingsToken && state.savingsCents <= 0) {
        if (row instanceof HTMLElement) row.hidden = true;
        return;
      }
      if (row instanceof HTMLElement) row.hidden = false;
      node.textContent = template.replaceAll('[economie]', formatMoney(state.savingsCents));
    });

    const accept = this.#modal?.querySelector('[data-kalaboost-upsell-accept-label]');
    if (accept instanceof HTMLElement) {
      accept.textContent = 'Compléter ma routine';
    }
  }

  /**
   * @param {object} cart
   */
  #routineState(cart) {
    const steps = this.#packComponents().map((component) => {
      const line = this.#componentLine(cart, component);
      return {
        ...component,
        inCart: Boolean(line),
        paidCents: line ? Number(line.final_price) || Number(component.priceCents) || 0 : 0,
      };
    });

    const present = steps.filter((step) => step.inCart);
    const packCents = Number(this.#config.pack?.priceCents) || 0;
    const packListCents = Math.max(
      Number(this.#config.pack?.compareAtPriceCents) || 0,
      packCents
    );
    const brushCents = Number(this.#config.brush?.priceCents) || 0;
    const brushListCents = Math.max(
      Number(this.#config.brush?.compareAtPriceCents) || 0,
      brushCents
    );
    const listCents = packListCents + brushListCents;
    const deductedCents = present.reduce((sum, step) => sum + step.paidCents, 0);
    const remainingCents = Math.max(0, packCents - deductedCents);

    return {
      steps,
      present,
      missingCount: steps.length - present.length,
      packPrice: formatMoney(packCents),
      listPrice: formatMoney(listCents),
      brushPrice: formatMoney(brushCents),
      savingsCents: Math.max(0, listCents - packCents),
      remainingCents,
    };
  }

  /**
   * @param {{ steps: object[] }} state
   */
  #renderBundle(state) {
    const row = this.#modal?.querySelector('[data-kalaboost-upsell-steps]');
    if (!(row instanceof HTMLElement)) return;

    row.replaceChildren();

    const cards = [
      ...state.steps.map((step) => ({
        name: step.name || step.title || '',
        image: step.image,
        inCart: step.inCart,
        gift: false,
      })),
      {
        name: this.#config.brush?.name || 'Brosse',
        image: this.#config.brush?.image,
        inCart: false,
        gift: true,
      },
    ];

    cards.forEach((card, index) => {
      if (index > 0) {
        const plus = document.createElement('span');
        plus.className = 'kb-routine-bundle__plus';
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';
        row.appendChild(plus);
      }

      row.appendChild(this.#createBundleCard(card));
    });
  }

  /**
   * @param {{ name: string, image?: string, inCart: boolean, gift: boolean }} card
   */
  #createBundleCard(card) {
    const wrap = document.createElement('article');
    wrap.className = `kb-routine-card${card.gift ? ' kb-routine-card--gift' : ''}`;

    if (card.gift) {
      const ribbon = document.createElement('span');
      ribbon.className = 'kb-routine-card__ribbon';
      ribbon.textContent = 'Offerte';
      wrap.appendChild(ribbon);
    }

    const frame = document.createElement('div');
    frame.className = 'kb-routine-card__frame';

    if (card.image) {
      const image = document.createElement('img');
      image.className = 'kb-routine-card__image';
      image.src = card.image;
      image.alt = card.name;
      frame.appendChild(image);
    } else {
      const empty = document.createElement('span');
      empty.className = 'kb-routine-card__empty';
      empty.setAttribute('aria-hidden', 'true');
      frame.appendChild(empty);
    }

    const name = document.createElement('p');
    name.className = 'kb-routine-card__name';
    name.textContent = card.name;

    wrap.append(frame, name);

    if (card.inCart) {
      const badge = document.createElement('span');
      badge.className = 'kb-routine-card__badge';
      badge.textContent = 'Dans le panier';
      wrap.appendChild(badge);
    }

    return wrap;
  }

  #openModal() {
    if (!this.#modal || this.#open) return;

    this.#lastFocus = document.activeElement instanceof Element ? document.activeElement : null;
    document.body.appendChild(this.#modal);
    this.#modal.hidden = false;
    document.documentElement.classList.add(OPEN_CLASS);
    this.#open = true;

    requestAnimationFrame(() => this.#modal?.classList.add('is-open'));
    this.#modal.querySelector(CLOSE_SELECTOR)?.focus?.();
  }

  #closeModal() {
    if (!this.#modal || !this.#open) return;

    this.#modal.classList.remove('is-open');
    document.documentElement.classList.remove(OPEN_CLASS);
    this.#open = false;

    this.#modal.hidden = true;
    if (this.#modalHome?.isConnected) this.#modalHome.appendChild(this.#modal);
    else this.#modal.remove();

    if (this.#lastFocus instanceof HTMLElement) this.#lastFocus.focus();
    this.#lastFocus = null;
  }

  #closeAndOpenCart() {
    this.#closeModal();
    this.#resumeCartDrawer();
    this.#openCartDrawer();
  }

  #pauseCartDrawer() {
    const component = document.querySelector('cart-drawer-component');
    if (!(component instanceof HTMLElement) || !component.hasAttribute('auto-open')) return;
    component.removeAttribute('auto-open');
    this.#drawerPaused = true;
  }

  #resumeCartDrawer() {
    if (!this.#drawerPaused) return;
    document.querySelector('cart-drawer-component')?.setAttribute('auto-open', '');
    this.#drawerPaused = false;
  }

  #openCartDrawer() {
    const drawer = document.getElementById('cart-drawer');
    drawer?.open?.();
  }

  /**
   * @param {number} variantId
   */
  async #addVariant(variantId) {
    const id = toId(variantId);
    if (!id) throw new Error('Variante manquante');

    const payload = {
      items: [{ id, quantity: 1 }],
      sections: this.#sectionIds(),
      sections_url: window.location.pathname,
    };

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
        detail: { source: SOURCE },
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify(payload) }));
      const data = await response.json();

      if (data.errors || data.status) {
        const message =
          typeof data.errors === 'string' ? data.errors : data.description || data.message || 'Erreur panier';
        throw new Error(message);
      }

      const cart = this.#isAjaxCart(data) ? data : await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { source: SOURCE, sections: data.sections, didError: false },
      });
      await waitFrames(2);
      return cart;
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  }

  async #swapToPack() {
    const pack = this.#config.pack;
    const components = this.#packComponents();
    if (!pack?.variantId) throw new Error('Pack manquant');
    if (!components.length) throw new Error('Trio pack incomplet');

    await this.#addVariant(pack.variantId);
    const cart = await this.#fetchCart();
    const updates = this.#oneOffEachComponent(cart, components);
    if (!Object.keys(updates).length) return cart;

    return this.#updateQuantities(updates);
  }

  /**
   * @returns {object[]}
   */
  #packComponents() {
    const listed = this.#config.components;
    if (!Array.isArray(listed)) return [];
    return listed.filter((item) => item && (item.handle || item.id));
  }

  /**
   * @param {object} cart
   * @param {object} component
   */
  #componentLine(cart, component) {
    const packHandle = this.#config.pack?.handle;
    const packId = this.#config.pack?.id;

    return (cart?.items || []).find((item) => {
      if (!item || item.quantity <= 0) return false;
      if (packHandle && item.handle === packHandle) return false;
      if (packId && Number(item.product_id) === Number(packId)) return false;
      return this.#lineMatchesComponent(item, component);
    });
  }

  /**
   * @param {object} cart
   * @param {object[]} components
   * @returns {Record<string, number>}
   */
  #oneOffEachComponent(cart, components) {
    const updates = {};
    const usedKeys = new Set();

    for (const component of components) {
      const line = (cart?.items || []).find((item) => {
        if (!item || item.quantity <= 0) return false;
        if (item.key && usedKeys.has(item.key)) return false;
        if (this.#config.pack?.handle && item.handle === this.#config.pack.handle) return false;
        return this.#lineMatchesComponent(item, component);
      });
      if (!line) continue;

      if (line.key) usedKeys.add(line.key);
      const key = line.key || String(line.variant_id || line.id);
      updates[key] = Math.max(0, line.quantity - 1);
    }

    return updates;
  }

  /**
   * @param {object} line
   * @param {object} component
   */
  #lineMatchesComponent(line, component) {
    if (component.handle && line.handle === component.handle) return true;
    if (component.id && Number(line.product_id) === Number(component.id)) return true;
    return false;
  }

  /**
   * @param {Record<string, number>} updates
   */
  async #updateQuantities(updates) {
    const payload = {
      updates,
      sections: this.#sectionIds(),
      sections_url: window.location.pathname,
    };

    const lines = Object.entries(updates).map(([id, quantity]) => ({
      merchandiseId: String(id),
      quantity,
    }));

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines,
        promise: deferred.promise,
        detail: { source: SOURCE },
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify(payload) }));
      const data = await response.json();

      if (data.errors || data.status) {
        const message =
          typeof data.errors === 'string' ? data.errors : data.description || data.message || 'Erreur panier';
        throw new Error(message);
      }

      const cart = this.#isAjaxCart(data) ? data : await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { source: SOURCE, sections: data.sections, didError: false },
      });
      await waitFrames(2);
      return cart;
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Lecture panier impossible (${response.status})`);
    return response.json();
  }

  /**
   * @param {object} cart
   * @returns {Set<string>}
   */
  #cartHandles(cart) {
    const handles = new Set();
    for (const item of cart?.items || []) {
      if (item?.handle) handles.add(item.handle);
    }
    return handles;
  }

  #sectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) ids.add(node.dataset.sectionId);
    });
    return Array.from(ids).join(',');
  }

  /**
   * @param {object} data
   */
  #isAjaxCart(data) {
    return Boolean(data && Array.isArray(data.items) && typeof data.item_count === 'number');
  }

  /**
   * @param {string} selector
   * @param {string} value
   */
  #setText(selector, value) {
    const node = this.#modal?.querySelector(selector);
    if (!(node instanceof HTMLElement)) return;
    node.textContent = value || '';
  }

  /**
   * @param {Element | null} node
   * @param {string} message
   */
  #setError(node, message) {
    if (!(node instanceof HTMLElement)) return;
    node.textContent = message;
    node.hidden = message === '';
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.#busy = busy;
    const buttons = [
      ...this.#root.querySelectorAll(ADD_SELECTOR),
      ...(this.#modal?.querySelectorAll(`${ACCEPT_SELECTOR}, ${DECLINE_SELECTOR}`) || []),
    ];
    buttons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      if (busy) {
        button.setAttribute('disabled', '');
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-busy');
      }
    });
  }

  destroy() {
    this.#closeModal();
    this.#resumeCartDrawer();
    this.#root.removeEventListener('click', this.#onClick);
    this.#modal?.removeEventListener('click', this.#onClick);
    document.removeEventListener('keydown', this.#onKeydown);
  }
}

const instances = new WeakMap();

function init(scope = document) {
  scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
    if (instances.has(root)) return;
    instances.set(root, new KalaboostProductUpsell(root));
  });
}

document.addEventListener('shopify:section:unload', (event) => {
  event.target?.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => {
    instances.get(root)?.destroy();
    instances.delete(root);
  });
});

document.addEventListener('shopify:section:load', (event) => init(event.target));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(), { once: true });
} else {
  init();
}
